import React from "react";
import { Anchor, Badge, Group, Stack, Text, Tooltip } from "@mantine/core";
import moment from "moment";
import { getFileUrl } from "../components/fileList";
import { ENTITY_BACKUP_TYPE_META } from "./fileBackupSettingsUtils";
import { resolveEntityPath } from "../utils/resolveEntityPath";

const ENTITY_LABELS = Object.fromEntries(
  ENTITY_BACKUP_TYPE_META.map((entry) => [entry.key, entry.label])
);

const FILE_NAME_MAX = 20;

const formatJobDateTime = (value) => {
  if (!value) return "";
  return moment(value).format("YYYY-MM-DD HH:mm:ss");
};

const truncateFileName = (text) => {
  if (!text) return "";
  if (text.length <= FILE_NAME_MAX) return text;
  return `${text.slice(0, FILE_NAME_MAX - 3)}...`;
};

const shortenJobError = (error) => {
  if (!error) return "";
  const text = String(error).trim();
  if (/local file missing/i.test(text)) return "Local file missing for backup";
  const beforeColon = text.split(":")[0]?.trim();
  if (beforeColon && beforeColon.length <= 48) return beforeColon;
  return text.length > 48 ? `${text.slice(0, 45)}...` : text;
};

const linkProps = {
  underline: false,
  color: "inherit",
  sx: {
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "none",
      color: "inherit",
    },
  },
};

const JobStatusBadge = ({ status }) => {
  if (!status) return null;
  const colors = {
    pending: "yellow",
    processing: "blue",
    synced: "green",
    failed: "red",
  };
  return (
    <Badge color={colors[status] || "gray"} size="xs" radius={0}>
      {String(status).toUpperCase()}
    </Badge>
  );
};

const JobActionBadge = ({ action }) => {
  if (!action) return null;
  const colors = {
    upload: "teal",
    delete: "orange",
    rename: "violet",
  };
  return (
    <Badge color={colors[action] || "gray"} size="xs" radius={0}>
      {String(action).toUpperCase()}
    </Badge>
  );
};

const openFile = (data) => {
  const fileUrl = getFileUrl({ link: data.localLink, url: data.url });
  if (!fileUrl) return;
  window.open(fileUrl, "_blank", "noopener,noreferrer");
};

const openEntity = ({ data, tabNav, userRole, currentPathname }) => {
  if (!data?.entityType || !data?.entityId || !tabNav) return;
  const pathname = resolveEntityPath(data.entityType, userRole, currentPathname);
  if (!pathname) return;
  tabNav.openDetail({
    pathname,
    id: data.entityId,
    mode: "edit",
    label: data.entityCode || ENTITY_LABELS[data.entityType] || data.entityType,
  });
};

const FileNameCell = ({ data, fileName, canOpenFile }) => {
  const displayName = truncateFileName(fileName);
  const showTooltip = fileName.length > FILE_NAME_MAX;

  const content = canOpenFile ? (
    <Anchor
      size="sm"
      {...linkProps}
      onClick={(event) => {
        event.preventDefault();
        openFile(data);
      }}
    >
      {displayName}
    </Anchor>
  ) : (
    <Text size="sm">{displayName}</Text>
  );

  if (!showTooltip) return content;

  return (
    <Tooltip label={fileName} withArrow openDelay={300}>
      <span>{content}</span>
    </Tooltip>
  );
};

export const FILE_BACKUP_JOB_SEARCH_FIELDS = [
  "fileName",
  "entityCode",
  "localLink",
  "remoteFolderPath",
  "lastError",
  "entityType",
];

export const getFileBackupJobTableSchema = ({
  tabNav,
  userRole,
  currentPathname = "",
} = {}) => ({
  name: "fileBackupSyncJobs",
  searchableFields: FILE_BACKUP_JOB_SEARCH_FIELDS,
  columns: [
    {
      field: "status",
      headerName: "Status",
      filterOnly: true,
      filterable: true,
      filter: {
        apiEntity: "fileBackupJob",
        field: "status",
        label: "Status",
      },
    },
    {
      field: "action",
      headerName: "Action",
      filterOnly: true,
      filterable: true,
      filter: {
        apiEntity: "fileBackupJob",
        field: "action",
        label: "Action",
      },
    },
    {
      field: "createdAt",
      headerName: "Date",
      sortable: true,
      minWidth: 200,
      width: 200,
      cellRender: (col, data) => (
        <Stack spacing={4}>
          <Group spacing={4} noWrap>
            <JobStatusBadge status={data.status} />
            <JobActionBadge action={data.action} />
          </Group>
          {data.createdAt ? (
            <Text size="xs" color="dimmed" sx={{ lineHeight: 1.2, whiteSpace: "nowrap" }}>
              {formatJobDateTime(data.createdAt)}
            </Text>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "fileName",
      headerName: "File",
      sortable: true,
      minWidth: 280,
      cellRender: (col, data) => {
        const fileName =
          data.fileName || data.localLink?.split("/").pop() || "";
        const entityLabel = ENTITY_LABELS[data.entityType] || data.entityType;
        const canOpenFile = Boolean(
          fileName && getFileUrl({ link: data.localLink, url: data.url })
        );
        const canOpenEntity = Boolean(
          data.entityType &&
            data.entityId &&
            resolveEntityPath(data.entityType, userRole, currentPathname)
        );
        const shortError = shortenJobError(data.lastError);

        return (
          <Stack spacing={2} sx={{ lineHeight: 1.15 }}>
            {fileName ? (
              <FileNameCell
                data={data}
                fileName={fileName}
                canOpenFile={canOpenFile}
              />
            ) : null}

            {data.entityType && data.entityCode ? (
              <Group spacing={4} align="center" noWrap sx={{ lineHeight: 1.15 }}>
                {canOpenEntity ? (
                  <>
                    <Anchor
                      size="xs"
                      {...linkProps}
                      onClick={(event) => {
                        event.preventDefault();
                        openEntity({ data, tabNav, userRole, currentPathname });
                      }}
                    >
                      {entityLabel}
                    </Anchor>
                    <Text size="xs" color="dimmed">
                      &gt;
                    </Text>
                    <Anchor
                      size="xs"
                      {...linkProps}
                      onClick={(event) => {
                        event.preventDefault();
                        openEntity({ data, tabNav, userRole, currentPathname });
                      }}
                    >
                      {data.entityCode}
                    </Anchor>
                  </>
                ) : (
                  <Text size="xs" color="dimmed">
                    {entityLabel} &gt; {data.entityCode}
                  </Text>
                )}
              </Group>
            ) : null}

            {shortError ? (
              <Text size="xs" color="red" sx={{ lineHeight: 1.15 }}>
                {shortError}
              </Text>
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: "remoteFolderPath",
      headerName: "Drive Path",
      sortable: true,
      minWidth: 220,
      cellRender: (col, data) =>
        data.remoteFolderPath ? (
          <Text size="xs" color="dimmed" sx={{ lineHeight: 1.15 }}>
            {data.remoteFolderPath}
          </Text>
        ) : null,
    },
    {
      field: "processedAt",
      headerName: "Completed At",
      sortable: true,
      minWidth: 160,
      width: 160,
      cellRender: (col, data) => (
        <Text size="xs">{formatJobDateTime(data.processedAt) || "-"}</Text>
      ),
    },
    {
      field: "attempts",
      headerName: "Attempts",
      sortable: true,
      minWidth: 90,
      width: 90,
      cellRender: (col, data) => (
        <Text
          size="sm"
          weight={500}
          color={data.status === "failed" ? "red" : undefined}
        >
          {data.attempts ?? 0}
        </Text>
      ),
    },
  ],
});
